import { getFontEmbedCSS, toCanvas } from 'html-to-image';

import { getExportBackground } from './exportBackground';

/** html-to-image does not export its `Options` type from the package entry. */
type HtmlToImageOptions = NonNullable<Parameters<typeof toCanvas>[1]>;

/**
 * Marks a node as chrome that must not appear in a DOM-to-image capture, whether it is copied,
 * downloaded as an image or exported to PDF. Excluding a node also excludes its children.
 */
export const COPY_EXCLUDE_ATTR = 'data-copy-exclude';

/**
 * Predicate for html-to-image's `filter` option. Kept separate so it can be unit tested
 * without a real capture.
 */
export function shouldIncludeNode(domNode: unknown): boolean {
    if (!(domNode instanceof HTMLElement)) {
        return true;
    }

    return !domNode.hasAttribute(COPY_EXCLUDE_ATTR);
}

/**
 * The font families the subtree renders with, as a stable key. html-to-image embeds only the
 * `@font-face` rules whose family the captured node uses, and the app loads each BoxedRound
 * weight as its own family, so the embedded CSS differs between a chart and a report.
 */
export function usedFontFamilies(element: HTMLElement): string {
    const view = element.ownerDocument.defaultView;
    if (!view) return '';

    const families = new Set<string>();
    for (const node of [element, ...Array.from(element.querySelectorAll('*'))]) {
        for (const family of view.getComputedStyle(node).fontFamily.split(',')) {
            const name = family.trim().replace(/^["']|["']$/g, '');
            if (name) families.add(name);
        }
    }
    return Array.from(families).sort().join(',');
}

/**
 * Embedding fonts means fetching and base64ing the font files, which dominates the cost of a
 * capture, so each result is computed once per set of families and shared across captures.
 * A failure resolves to an empty string, which degrades to system fonts rather than failing
 * the whole capture.
 */
const fontEmbedCSSCache = new Map<string, Promise<string>>();

function getCachedFontEmbedCSS(element: HTMLElement): Promise<string> {
    const key = usedFontFamilies(element);
    const cached = fontEmbedCSSCache.get(key);
    if (cached) return cached;

    const pending = getFontEmbedCSS(element).catch((error: unknown) => {
        console.error('Failed to embed fonts for image capture:', error);
        return '';
    });
    fontEmbedCSSCache.set(key, pending);
    return pending;
}

export interface CaptureElementOptions {
    /** Any valid CSS color. Defaults to the surface of the theme on screen. */
    backgroundColor?: string;
    /** Scale factor of the bitmap. 2 keeps text crisp when pasted or printed. */
    pixelRatio?: number;
    /**
     * Space around the content, in CSS pixels, painted in the background colour. Without it,
     * borders and headings sit on the image edge, and text that renders a pixel wider in the
     * clone (embedded fonts) is clipped at the right.
     */
    margin?: number;
}

const DEFAULT_PIXEL_RATIO = 2;

/**
 * Rasterises a DOM subtree to a canvas, skipping any node marked with
 * {@link COPY_EXCLUDE_ATTR}. The element is captured at its current layout, so whatever the
 * user has expanded is what ends up in the image.
 *
 * The canvas is opaque, on the surface colour of the theme on screen unless the caller picks
 * another, read when the capture runs so a capture right after a theme switch still matches
 * the page.
 *
 * html-to-image draws the clone through an SVG `foreignObject`, so the browser itself paints
 * the computed styles: CSS variables, `color-mix()` tokens and `color()` values come out as on
 * screen in both themes, with no colour parsing of its own to trip over.
 *
 * html-to-image scales a canvas down to fit 16384 px on its longest side, so the bitmap can be
 * smaller than `pixelRatio` asks for; callers size from the canvas, not from the ratio.
 */
export async function captureElement(
    element: HTMLElement,
    options: CaptureElementOptions = {}
): Promise<HTMLCanvasElement> {
    const {
        backgroundColor = getExportBackground(),
        pixelRatio = DEFAULT_PIXEL_RATIO,
        margin = 0,
    } = options;

    const fontEmbedCSS = await getCachedFontEmbedCSS(element);

    const captureOptions: HtmlToImageOptions = {
        filter: shouldIncludeNode,
        pixelRatio,
        backgroundColor,
        cacheBust: false,
        fontEmbedCSS,
    };
    if (margin > 0) {
        // The clone keeps its content width inside a border-box padded by the margin.
        captureOptions.width = element.offsetWidth + 2 * margin;
        captureOptions.height = element.offsetHeight + 2 * margin;
        captureOptions.style = { padding: `${margin}px`, boxSizing: 'border-box', margin: '0' };
    }

    return toCanvas(element, captureOptions);
}
